import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
} from '@chakra-ui/react'
import { useFormik } from 'formik'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { IoEye, IoEyeOff } from 'react-icons/io5'
import { useIamStore } from 'src/modules/iam/apis/store'
import { logger } from 'src/shared/Logger'
import * as Yup from 'yup'
import useNewPasswordRequired from '../../hooks/useNewPasswordRequired'
import ValidatePassword from '../ValidatePassword'

export const LOGIN_CONFIRM_EMAIL_VERIFICATION_TEST_ID = Object.freeze({
  button: 'confirm-email-verification-testid',
  resend: 'resend-email-verification-testid',
})

const RegisterNewPassword: React.FC = () => {
  const [t] = useTranslation()
  const { send } = useIamStore((store) => ({ send: store.sendEvent }))
  const { isLoading, email, confirmNewPassword, isCounting, timer, sendRecoverPasswordEmail } = useNewPasswordRequired()
  const [isValid, setValidation] = React.useState(false)
  const [showPassword, setShowPassword] = React.useState(false)
  const Validation = Yup.object().shape({
    password: Yup.string()
      .min(8)
      .matches(/[a-z]/)
      .matches(/[A-Z]/)
      .matches(/[0-9]/)
      .matches(/[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/),
  })
  const form = useFormik({
    initialValues: {
      email: email,
      password: '',
      code: '',
    },
    validationSchema: Validation,
    onSubmit: (values) => {
      logger.info('sending new password required data', { email })
      confirmNewPassword({ email: values.email, password: values.password, code: String(values.code) })
    },
  })

  const goToLogin = () => {
    send({ type: 'BACK' })
  }

  return (
    <>
      <form onSubmit={form.handleSubmit}>
        <Stack alignItems='center' spacing={4} maxW='400px'>
          <Image src='/assets/images/lock.svg' w='220px' />
          <Text as='h3' fontSize='xl' fontWeight='extrabold'>
            {t('login.redefinePassword.Redefine password')}
          </Text>
          <Text textAlign='center'>{t('login.redefinePassword.new password description')}</Text>
          <FormControl id='email' isInvalid={!!(form.errors.email && form.touched.email)}>
            <FormLabel
              htmlFor='email'
              fontFamily='Mulish'
              fontWeight='700'
              color='gray.800'
              fontSize='16px'
              marginBottom='2px'
            >
              {t('login.E-mail')}
            </FormLabel>
            <Input type='email' name='email' borderRadius='16px' disabled value={form.values.email} />
          </FormControl>
          <FormControl id='code'>
            <FormLabel htmlFor='code' fontFamily='Mulish' fontWeight='extrabold'>
              {t('login.redefinePassword.code')}
            </FormLabel>
            <Input type='text' placeholder={'000000'} w='100%' onChange={form.handleChange} maxLength={6} />
            <FormErrorMessage>{form.errors.code}</FormErrorMessage>
          </FormControl>
          <FormControl id='password' isInvalid={!!(form.errors.password && form.touched.password)}>
            <FormLabel htmlFor='password' fontFamily='Mulish' fontWeight='extrabold'>
              {t('onboard.registration.Password')}
            </FormLabel>
            <InputGroup>
              <Input
                type={showPassword ? 'text' : 'password'}
                autoComplete='new-password'
                id='new-password'
                placeholder={t('account.changePassword.Repeat the password')}
                w='100%'
                name={'password'}
                onChange={form.handleChange}
                value={form.values.password}
              />
              <InputRightElement width='4.5rem'>
                <IconButton
                  as='div'
                  cursor='pointer'
                  icon={showPassword ? <IoEyeOff /> : <IoEye />}
                  backgroundColor='transparent'
                  _hover={{
                    bg: 'transparent',
                  }}
                  _active={{
                    bg: 'transparent',
                  }}
                  _focus={{
                    bg: 'transparent',
                  }}
                  fontSize='xl'
                  color='gray.700'
                  size='sm'
                  fontFamily='Poppins'
                  fontWeight='bold'
                  aria-label='Show/hide password'
                  onClick={() => setShowPassword((data) => !data)}
                  type='button'
                ></IconButton>
              </InputRightElement>
            </InputGroup>
          </FormControl>
          <ValidatePassword password={form.values.password} onChange={(valid) => setValidation(valid)} />
          <Button size='lg' type='submit' width='100%' isLoading={isLoading} isDisabled={isLoading || !isValid}>
            {t('login.redefinePassword.button')}
          </Button>
          <Button
            w='100%'
            disabled={isCounting}
            isLoading={isLoading}
            onClick={sendRecoverPasswordEmail}
            variant={'outline'}
            data-testid={LOGIN_CONFIRM_EMAIL_VERIFICATION_TEST_ID.resend}
            marginTop={6}
          >
            {isCounting
              ? `${t('login.redefinePassword.resend')} (${String(timer)})`
              : t('login.redefinePassword.resend')}
          </Button>
          <Button variant='startCourseDark' w='100%' onClick={goToLogin}>
            {t('invalid sign up.button')}
          </Button>
        </Stack>
      </form>
    </>
  )
}

export default RegisterNewPassword
